import { Component, useEffect, useRef, type ReactNode } from 'react';
import './render-boundary.css';

interface Props {
  children: ReactNode;
  scope: 'page' | 'app';
  resetKey?: string;
}

/** Keep page failures below Workshop so drafts and running solvers stay alive. */
export class RenderBoundary extends Component<Props, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidUpdate(previous: Props) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <RenderFailure scope={this.props.scope} onRetry={() => this.setState({ failed: false })} />;
  }
}

function RenderFailure({ scope, onRetry }: { scope: Props['scope']; onRetry(): void }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);
  const page = scope === 'page';
  return <section className={`render-failure render-failure-${scope}`} role="alert">
    <div>
      <h2 ref={heading} tabIndex={-1}>{page ? '当前页面暂时无法显示' : '命轮工坊暂时无法显示'}</h2>
      <p>{page ? '工作台仍在运行，可以重试当前页面，或通过导航打开其他页面。' : '页面运行遇到异常，请重新加载。已保存的方案和结果不会被清除。'}</p>
      {!page && <p>重新加载会中断本页计算，尚未保存的输入可能丢失。</p>}
      <button type="button" onClick={page ? onRetry : () => window.location.reload()}>{page ? '重试当前页面' : '重新加载'}</button>
      <p className="render-failure-help">若仍无法打开，请更新浏览器，并将控制台中的错误信息反馈给管理员。</p>
    </div>
  </section>;
}
